/**
 * Product copy for a PMS bind failure — the reader-facing form of
 * `PmsContactMatterBinding.lastError` (VALE-158).
 *
 * **The stored string is the provider's, and it stays that way.**
 * `recordBindFailure` persists whatever the PMS said, which is right for an
 * audit column and wrong for a solicitor: ActionStep answers a rejected
 * participant with *"Error while trying to validate record"*, which names no
 * cause, suggests no action, and reads as an internal fault rather than a
 * choice the user can change.
 *
 * So it is **mapped at the presentation layer, never rewritten at the source** —
 * the same rule `docs/attribute-treatment.md` §2 states for save failures
 * (*"failure copy is mapped, never the raw wire string"*) and that
 * `handleMutationError` already implements for HTTP statuses. The raw string
 * stays in the column and in `customer_integration_sync_logs`, so support loses
 * nothing; callers surface it as a `title` so it is one hover away.
 *
 * **Unrecognised errors pass through untouched.** A blanket replacement would
 * hide a genuinely different failure — an auth lapse, a network fault, a
 * provider outage — behind a confident guess about participant types. Only the
 * shape we can actually attribute is rewritten.
 */

/**
 * ActionStep's rejection when a participant type is not accepted on the
 * matter's action type. It is a *validation* refusal rather than a fault, and
 * it is the single failure the support caveat beside the picker exists to warn
 * about — the offered catalogue cannot be filtered to the valid subset, because
 * `actiontypeparticipanttypes` is unreachable on our app registration.
 *
 * Matched on the distinctive fragment rather than the whole sentence, so
 * incidental punctuation or a wrapping prefix does not silently fall through to
 * the raw string.
 */
const VALIDATE_RECORD_REJECTION = /validate record/i;

/**
 * What we tell the reader when the PMS refused the participant type — the
 * approved design's own wording.
 *
 * **It states the cause rather than asking about it.** ActionStep rejects a
 * participant when the type is not among those its *action type* accepts, so
 * naming that is both the accurate diagnosis and the actionable one: the reader
 * changes the participant type, or the matter type has to. A question ("is this
 * contact type allowed?") describes the same fact while making the product
 * sound unsure of an answer it already has.
 *
 * It also states no failure of its own, because every caller opens with the
 * failure it is actually looking at — "Assignment failed" where nothing was
 * ever filed, "Last sync failed" where a filed contact's re-bind did not land.
 * Carrying a second "failed" in here is what produced *"Last sync failed:
 * Assignment failed, …"*.
 */
export const PMS_BIND_REJECTED_COPY =
  'Action type does not accept this participant type';

/**
 * The message to show for a stored bind error, or `null` when there is none.
 *
 * Returns the provider's own string for anything it cannot attribute — see the
 * module header for why that is deliberate rather than a gap. That string is a
 * sentence of its own, so it reads correctly after the caller's lead-in too.
 */
export function pmsBindErrorCopy(
  lastError: string | null | undefined
): string | null {
  if (!lastError) return null;
  return VALIDATE_RECORD_REJECTION.test(lastError)
    ? PMS_BIND_REJECTED_COPY
    : lastError;
}
